import type { LabelLayerGroup } from '@/composables/useThreeDLayerGroups'
import { useThreeDLayerGroups } from '@/composables/useThreeDLayerGroups'
import type { DrillDataSetLayerGroup } from '@/composables/useDrillDataSetGroups'
import { useDrillDataSetGroups } from '@/composables/useDrillDataSetGroups'
import type { UseThreeDRepo } from '@/composables/useThreeDRepo'
import { useThreeDRepo } from '@/composables/useThreeDRepo'
import type { ViewerControls } from '@/lib/three-d-resources/controls/viewerControls'
import { LabelsManager } from '@/lib/three-d-resources/labels-manager'
import type { TextLabel } from '@/lib/three-d-resources/labels-manager'
import type { SceneConfig } from '@/lib/three-d-resources/scene-config'
import { ThreeDResourceManager } from '@/lib/three-d-resources/three-d-resource-manager'
import { World } from '@/lib/three-d-resources/world'
import type { DeepReadonly, Ref } from 'vue'
import { BufferGeometry, Mesh, PerspectiveCamera, Vector2 } from 'three'
import { acceleratedRaycast, computeBoundsTree, disposeBoundsTree } from 'three-mesh-bvh'
import type { ModelView } from '~/entities/meeting'
import setScene from './set-scene'
import type { CameraData, ClusterVisibility, DrillLabelVisibility, LabelVisibility, NodeVisibility } from '~/entities/model3d'
import { DrillDataSetManager } from '~/lib/three-d-resources/drill-data-set-manager'
import { TargetVisualizer } from '~/lib/three-d-resources/target-visualizer'
import { DEBUG } from '~/lib/three-d-resources/debugger'

export type LayerGroup = LabelLayerGroup | DrillDataSetLayerGroup

export function isDrillDataSetLayerGroup(layerGroup: LayerGroup): layerGroup is DrillDataSetLayerGroup {
  return layerGroup.type === 'drill'
}

export interface UseThreeDViewer {
  progress: Readonly<Ref<number>>
  hiddenLayerProgress: Readonly<Ref<number>>
  currentSceneConfig: Readonly<Ref<SceneConfig | null>>
  layerGroups: Readonly<Ref<LayerGroup[]>>
  threeDRepo?: UseThreeDRepo
  setActiveScene(modelId: number, cameraId: number | null, options?: { shouldTween?: boolean }): Promise<void>
  clearActiveScene(): void
  setLayerVisibility(param: NodeVisibility[]): void
  setDrillHoleVisibility(param: DrillLabelVisibility[]): void
  onLabelResize(params: { width: number; height: number; label: TextLabel }): void
  error: Readonly<Ref<string | undefined>>
  controls: ViewerControls
  cleanupThreeDResources: () => void
  world: World
  labelsManager: LabelsManager
  downloadImageFile(modelId: number, width: number | null, height: number | null): Promise<void>
  search: Readonly<Ref<string>>
  setSearch(search: string): void
}

// raycasting optimizations from https://github.com/gkjohnson/three-mesh-bvh
BufferGeometry.prototype.computeBoundsTree = computeBoundsTree
BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree
Mesh.prototype.raycast = acceleratedRaycast

export function useThreeDViewer({
  currentView,
  priorityCameras,
  canLoad = ref(true),
  accessType = 'view',
  onFirstModelVisible = () => {},
  onSetLayerVisibility,
  onDrillLabelToggleCollapsed,
  onSetScene,
  onConfirmLabelsDelete,
  maxAge,
  skipHiddenNodes = false,
  onSaved,
}: {
  currentView?: Ref<DeepReadonly<ModelView>>
  priorityCameras: Ref<DeepReadonly<CameraData[]>>
  canLoad?: Ref<boolean>
  accessType?: 'edit' | 'view'
  onFirstModelVisible?(): void
  onSetLayerVisibility?(
    param: NodeVisibility[] | LabelVisibility[] | DrillLabelVisibility[] | ClusterVisibility[],
    source?: 'user' | 'system',
  ): void
  onDrillLabelToggleCollapsed?: (payload: { id: number; is_collapsed: boolean }) => void
  onSetScene?({ sceneConfig }: { sceneConfig: SceneConfig }): void
  onConfirmLabelsDelete?(): Promise<boolean>
  maxAge?: number
  skipHiddenNodes?: boolean
  onSaved?: (eventName: 'save3DSlide' | 'saveAs3DSlide') => void
}): UseThreeDViewer {
  const currentSceneConfig = shallowRef<SceneConfig | null>(null)
  const error = ref<string | undefined>(undefined)
  const progress = ref(0)
  const hiddenLayerProgress = ref(0)
  const route = useRoute()
  const { $analytics, $sentry } = useNuxtApp()

  const camera = new PerspectiveCamera(45, 1, 10, 10000)

  const world = new World(
    camera,
    () => {
      rebuildThreeDLayerGroups()
    },
    accessType,
  )
  DEBUG.init(world)
  DEBUG.get().set('transparency_time', 0)
  const search = ref('')

  const {
    layerGroups: threeDLayerGroups,
    rebuildLayerGroups: rebuildThreeDLayerGroups,
    clearLayerGroups: clearThreeDLayerGroups,
  } = useThreeDLayerGroups({ world, search })
  const {
    layerGroups: drillDataSetLayerGroups,
    rebuildLayerGroups: rebuildDrillDataSetLayerGroups,
    clearLayerGroups: clearDrillDataSetLayerGroups,
  } = useDrillDataSetGroups({ world, search })

  const labelsManager = new LabelsManager(
    {
      uuid: (route.query?.auth as string) ?? null,
    },
    rebuildThreeDLayerGroups,
    () => {
      world.needsRender = true
    },
    onSetLayerVisibility,
    'view',
    currentView,
  )
  world.setLabelsManager(labelsManager)

  const loaderConfig = {
    uuid: (route.query?.auth as string) ?? null,
  }

  const drillDataSetManager = new DrillDataSetManager(
    loaderConfig,
    () => {
      rebuildDrillDataSetLayerGroups()
      rebuildThreeDLayerGroups()
      world.needsRender = true
    },
    onSetLayerVisibility,
    onDrillLabelToggleCollapsed,
    currentView,
    maxAge,
  )
  world.setDrillDataSetManager(drillDataSetManager)

  const targetVisualizer = accessType === 'edit' ? new TargetVisualizer() : null
  if (targetVisualizer) world.setTargetVisualizer(targetVisualizer)

  const progressMap = ref<{ [key: number]: number | { [key: number]: number } }>({})

  function setThreeDResourceError(e: Error) {
    error.value = e.message
  }

  const threeDResourceManager = new ThreeDResourceManager(
    {
      analytics: $analytics,
      uuid: (route.query?.auth as string) ?? null,
      accessType,
    },
    targetVisualizer,
    // TODO: extract these functions out so they're testable
    ({ modelId, layerId }) => {
      if (world.currentModel?.id !== modelId || !world.currentSceneConfig) return

      world.controls.setMaxDistance(world.currentModel.sceneSize)

      // v2 model
      if (layerId) {
        let visible = world.currentSceneConfig.isVisible(layerId)

        if (currentView?.value.layer_visibilities.length) {
          const nodeName = world.currentModel.nodeIdToName.get(layerId)!
          const layerVisibility = currentView?.value.layer_visibilities.find(v => nodeName === v.layer_id)

          if (layerVisibility) {
            visible = layerVisibility.visible
          }
        } // DEPRECATED
        else if (currentView?.value.layers_hidden && currentView?.value.layers_visible) {
          switch (true) {
            case currentView?.value.layers_hidden?.includes(world.currentModel.nodeIdToName.get(layerId)!):
              visible = false
              break
            case currentView?.value.layers_visible?.includes(world.currentModel.nodeIdToName.get(layerId)!):
              visible = true
              break
          }
        } else if (world.changedLayers[layerId]) {
          visible = world.changedLayers[layerId]
        }

        world.setLayerVisibility([{ nodeId: layerId, visible }], { debounced: true })
      }

      const isV1ModelLoaded = !layerId && modelId

      if (isV1ModelLoaded && currentSceneConfig.value) {
        const nodeIdAndVisibility = []
        for (let [nodeId] of world.currentModel.layerNodesById) {
          let visible = currentSceneConfig.value.isVisible(nodeId)

          if (currentView?.value.layer_visibilities.length) {
            const nodeName = world.currentModel.nodeIdToName.get(nodeId)!
            const layerVisibility = currentView?.value.layer_visibilities.find(v => nodeName === v.layer_id)

            if (layerVisibility) {
              visible = layerVisibility.visible
            }
          } // DEPRECATED
          else if (currentView?.value.layers_hidden && currentView?.value.layers_visible) {
            switch (true) {
              case currentView?.value.layers_hidden?.includes(world.currentModel.nodeIdToName.get(nodeId)!):
                visible = false
                break
              case currentView?.value.layers_visible?.includes(world.currentModel.nodeIdToName.get(nodeId)!):
                visible = true
                break
            }
          }

          nodeIdAndVisibility.push({ nodeId, visible })
        }

        world.setLayerVisibility(nodeIdAndVisibility)
      }

      if (currentSceneConfig.value?.id === null) {
        world.controls.setCameraData(currentSceneConfig.value.cameraViewData, { tweenDuration: 0 })
      }

      currentView?.value?.layer_visibilities.forEach(l => {
        if (l.type !== 'drill-legend') return
        setDrillHoleVisibility([{ drillLabelId: l.layer_id, visible: l.visible }])
      })
    },
    (modelId, sceneId, newProgress) => {
      progressMap.value = {
        ...progressMap.value,
        [modelId]: sceneId
          ? {
              ...(progressMap.value[modelId] as { [key: number]: number }),
              [sceneId]: newProgress,
            }
          : newProgress,
      }
    },
    ({ cameraId }) => {
      world.render()
      onFirstModelVisible?.()

      // This is just for iOS while they're migrating to using the new
      // layer_visibilities field on the socket. They need the first scene to
      // be set properly and our normal one fires too early (model is still
      // loading).
      if (cameraId === currentSceneConfig?.value?.id) {
        onSetScene?.({ sceneConfig: currentSceneConfig.value! })
      }
    },
    setThreeDResourceError,
  )

  const threeDRepo = useThreeDRepo({
    world,
    labelsManager,
    threeDResourceManager,
    onConfirmLabelsDelete,
    clearLayerGroups: () => {
      clearThreeDLayerGroups()
      clearDrillDataSetLayerGroups()
    },
    onSaved,
  })

  world.controls.getCenterHitPoint = () => {
    const hit = world.hitTest(new Vector2(0)) // center screen
    if (!hit || hit.label) return null
    return hit.point
  }

  function setLayerVisibility(param: NodeVisibility[]) {
    world.setLayerVisibility(param)
    onSetLayerVisibility?.(param, 'user')
  }

  function setDrillHoleVisibility(param: DrillLabelVisibility[]) {
    param.forEach(({ drillLabelId, visible }) => {
      drillDataSetManager.drillLegendManager.setVisible(drillLabelId, visible)
    })
    rebuildDrillDataSetLayerGroups()
    onSetLayerVisibility?.(param, 'user')
  }

  function onLabelResize({ width, height, label }: { width: number; height: number; label: TextLabel }) {
    labelsManager.resizeCutout(label.id, width, height)
  }

  async function setActiveScene(modelId: number, cameraId: number | null, { shouldTween = true }: { shouldTween?: boolean } = {}) {
    setScene({
      world,
      labelsManager,
      drillDataSetManager,
      currentSceneConfig,
      threeDResourceManager,
      priorityCameras,
      currentView,
      onSetScene,
      setSearch: (newSearch: string) => (search.value = newSearch),
    })({ modelId, cameraId, shouldTween })
  }

  function clearActiveScene() {
    currentSceneConfig.value = null
  }

  function cleanupThreeDResources() {
    threeDResourceManager.resetResources()
    world.resetResources()
    labelsManager.resetData()
    drillDataSetManager.cleanUp()
    targetVisualizer?.resetData()
    currentSceneConfig.value = null
    progress.value = 0
    hiddenLayerProgress.value = 0
    progressMap.value = {}
  }

  watch([priorityCameras, canLoad], ([newPriorityCameras, newCanLoad]) => {
    if (newCanLoad) {
      threeDResourceManager.loadPriorityList(newPriorityCameras, skipHiddenNodes)

      if (newPriorityCameras.length === 0) {
        onFirstModelVisible?.()
      }
    }
  })

  watch([progressMap, currentSceneConfig], ([newProgressMap, newCurrentSceneConfig]) => {
    if (!newCurrentSceneConfig) {
      progress.value = 0
      return
    }

    const newProgress = newCurrentSceneConfig.calculateProgress(newProgressMap)
    const newHiddenProgress = newCurrentSceneConfig.calculateHiddenLayerProgress(newProgressMap)

    if (newProgress === 1) {
      labelsManager.toggleGroupVisibility(true)
      drillDataSetManager.toggleGroupVisibility(true)

      if (progress.value < 1) {
        rebuildThreeDLayerGroups()
      }
    }

    progress.value = newProgress
    hiddenLayerProgress.value = newHiddenProgress
  })

  async function downloadImageFile(modelId: number, width: number | null, height: number | null): Promise<void> {
    try {
      if (modelId !== world.currentModel?.id) {
        throw new Error('Viewer is not on the correct model')
      }

      const blob = await world.getThumbnailBlob({
        width: width ?? world.renderer.domElement.width,
        height: height ?? world.renderer.domElement.height,
      })

      const file = new File([blob], `${currentSceneConfig.value?.title.substring(0, 90)}.png`, { type: 'image/png' })

      const url = URL.createObjectURL(file)
      const a = document.createElement('a')
      a.style.display = 'none'
      a.href = url
      a.download = file?.name
      document.body.appendChild(a)
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      $sentry?.captureException(e)
      throw e
    }
  }

  return {
    progress,
    hiddenLayerProgress,
    cleanupThreeDResources,
    setActiveScene,
    clearActiveScene,
    controls: world.controls, // TODO: this can probably not be exposed directly and accessed through the world instead
    onLabelResize,
    layerGroups: computed(() => drillDataSetLayerGroups.value.concat(threeDLayerGroups.value as any)),
    setLayerVisibility,
    setDrillHoleVisibility,
    error: readonly(error),
    labelsManager,
    threeDRepo,
    world,
    currentSceneConfig,
    downloadImageFile,
    search: readonly(search),
    setSearch(newSearch: string) {
      search.value = newSearch
    },
  }
}
